<script setup lang="ts">
import type { Area, Menu } from "@gasparigit/omnia-sdk";
import { link } from "~/lib/utils/contents";

const props = withDefaults(
  defineProps<{
    area: Area;
    menu: Menu;
    level?: number;
    levels?: number[];
    chain?: Menu[];
    opened?: number[];
  }>(),
  {
    level: 0,
    levels: () => [],
    chain: () => [],
  }
);

const fontSize = computed(() => {
  if (props.level <= 0) return "text-xl font-semibold";
  if (props.level === 1) return "text-lg font-semibold";
  return "text-base";
});

const spanLevelClass = computed(() => {
  return {
    "font-normal text-white text-sm px-2 py-1 rounded-md": true,
    "bg-primary": props.level === 0,
    "bg-primary-600": props.level === 1,
    "bg-primary-700": props.level === 2,
    "bg-primary-800": props.level === 3,
    "bg-primary-900": props.level === 4,
    "bg-primary-950": props.level >= 5,
  };
});

const currentOpened = computed(() => {
  if (!props.opened) return;

  const opened = props.opened.at(props.level);
  if (!opened) return;

  return "menu-" + opened;
});

function isHighlighted(menu: Menu) {
  if (!props.opened) return false;

  const opened = props.opened.at(props.level);
  if (!opened) return false;
  if (opened !== props.opened.at(props.opened.length - 1)) return false;

  return opened === menu.id;
}

onMounted(() => {
  const documentsToScroll = document.getElementsByClassName("scroll-to-here");
  if (!documentsToScroll.length) return;

  const documentToScroll = documentsToScroll[0];
  const scrollToPosition = documentToScroll.getBoundingClientRect().top + window.scrollY - 100;

  setTimeout(() => {
    documentToScroll.classList.remove("scroll-to-here");
  }, 1200);

  setTimeout(() => {
    window.scrollTo({
      top: scrollToPosition,
      left: 0,
      behavior: "smooth",
    });
  }, 10);
});

function onOpened(val: string | string[]) {
  let refChain = props.chain.map((m) => (typeof m === "number" ? m : m.id));

  if (typeof val === "string") {
    const menuId = val.substring(5);
    refChain.push(parseInt(menuId));
  }

  useRouter().push({ query: { ...useRoute().query, ref: refChain.join(".") } });
}
</script>

<template>
  <div :class="level === 0 && 'grid gap-2'">
    <template v-for="(child, i) in menu.children">
      <NuxtLink
        v-if="child.type === 'hub'"
        :key="`hub-${child.id}`"
        :href="link({ area, hub: child, chain: chain })"
        class="px-5 block py-4 hover:text-primary-600 transition-colors"
        :class="[level === 0 ? 'bg-primary/5 rounded-sm border border-primary/15' : 'bg-background border-b-0 border-x-0 border-t border-t-primary/15', fontSize, level > 1 && 'pl-10']"
      >
        <div class="flex place-items-center gap-3">
          <span :class="spanLevelClass">{{ [...levels, i + 1].join(".") }}</span>
          <span>{{ child.title }}</span>
        </div>
      </NuxtLink>
      <template v-else>
        <Accordion type="single" collapsible class="w-full" :class="level === 0 && 'grid gap-2'" v-if="child.children?.length" :default-value="currentOpened" @update:model-value="onOpened">
          <AccordionItem
            :key="`menu-${child.id}`"
            :value="`menu-${child.id}`"
            class="transition-colors duration-1000 ease-in-out"
            :class="[level === 0 ? 'bg-primary/5 rounded-sm border border-primary/15' : 'bg-background border-b-0 border-x-0 border-t border-t-primary/15', isHighlighted(child) && 'scroll-to-here']"
          >
            <AccordionTrigger :class="['px-5 font-medium', fontSize, level > 1 && 'pl-10']">
              <div class="flex place-items-center gap-3">
                <span :class="spanLevelClass">{{ [...levels, i + 1].join(".") }}</span>
                <span>{{ child.title }}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent class="bg-primary/5 pb-0">
              <FrontMenuTree :menu="child" :area="area" :level="level + 1" :levels="levels.concat([i + 1])" :chain="chain.concat([child])" :opened="opened" />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </template>
    </template>
  </div>
</template>

<style lang="pcss">
.scroll-to-here {
  @apply bg-tertiary-50;
}
</style>
